import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Badge, Button, Card, EmptyState, Field, Input } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useLookupAccountMutation, useUnlockAccountMutation } from '../api';
import { KIND_LABEL_KEY, type AccountSummary } from '../types';

import { AccountSessionsPanel } from './AccountSessionsPanel';
import { PasswordActionDialog } from './PasswordActionDialog';
import { ResetAdminPasswordDialog } from './ResetAdminPasswordDialog';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { useSession } from '@/lib/session';

/**
 * Finds ONE account by its address (an audited read: the address itself is never written to the audit trail, only its fingerprint) and
 * offers what the support need requires on it: its sessions, ending them, unlocking it, and - for a platform ADMINISTRATOR only - a new
 * provisional password. A superadmin gets an explanation instead of the reset (never through the application); a shop owner's password
 * is reset from the merchant list.
 */
export function AccountLookup({ reason }: { readonly reason: string }): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { userId: me } = useSession();
  const lookup = useLookupAccountMutation();
  const unlockMutation = useUnlockAccountMutation();
  const [email, setEmail] = useState('');
  const [account, setAccount] = useState<AccountSummary | null | undefined>(undefined);
  const [resetting, setResetting] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const reasonValid = reason.trim().length >= 10;

  async function search(): Promise<void> {
    try {
      const result = await lookup.mutateAsync({ email: email.trim(), reason });
      setAccount(result.account);
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  const label =
    account === null || account === undefined ? '' : (account.displayName ?? account.email);
  const isLocked = account !== null && account !== undefined && account.lockedUntil !== null;

  return (
    <div className="flex flex-col gap-5">
      <form
        className="flex flex-wrap items-end gap-3 rounded-xl border border-line bg-surface p-4"
        onSubmit={(event) => {
          event.preventDefault();
          void search();
        }}
      >
        <div className="min-w-64 flex-1">
          <Field label={t('admin.access.lookup.emailLabel')}>
            {(bind) => (
              <Input
                {...bind}
                type="email"
                value={email}
                autoComplete="off"
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Button
          type="submit"
          loading={lookup.isPending}
          disabled={!reasonValid || email.trim() === ''}
        >
          {t('admin.access.lookup.submit')}
        </Button>
        {!reasonValid ? (
          <p className="w-full text-xs text-inkFaint">{t('admin.access.lookup.reasonFirst')}</p>
        ) : null}
      </form>

      {account === null ? <EmptyState title={t('admin.access.lookup.notFound')} /> : null}

      {account !== null && account !== undefined ? (
        <Card>
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-ink">{label}</p>
                <p className="text-sm text-inkFaint">{account.email}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge tone={account.kind === 'superadmin' ? 'accent' : 'neutral'}>
                    {t(KIND_LABEL_KEY[account.kind])}
                  </Badge>
                  <Badge tone={account.status === 'active' ? 'good' : 'critical'}>
                    {t(
                      account.status === 'active'
                        ? 'admin.accounts.status.active'
                        : 'admin.accounts.status.disabled',
                    )}
                  </Badge>
                  {account.lockedUntil !== null ? (
                    <Badge tone="warn">{`${t('admin.access.lookup.locked')} ${new Date(account.lockedUntil).toLocaleString('fr-TN')}`}</Badge>
                  ) : null}
                  {account.mustChangePassword ? (
                    <Badge tone="info">{t('admin.access.lookup.provisional')}</Badge>
                  ) : null}
                </div>
                <p className="mt-2 text-xs text-inkFaint">
                  {t('admin.access.lookup.lastLogin')} :{' '}
                  {account.lastLoginAt === null
                    ? t('admin.accounts.table.neverSignedIn')
                    : new Date(account.lastLoginAt).toLocaleString('fr-TN')}
                </p>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                {isLocked ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setUnlocking(true);
                    }}
                  >
                    {t('admin.access.action.unlock')}
                  </Button>
                ) : null}
                {account.kind === 'platform_admin' && account.userId !== me ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      setResetting(true);
                    }}
                  >
                    {t('admin.access.reset.action')}
                  </Button>
                ) : null}
              </div>
            </div>

            {account.kind === 'superadmin' ? (
              <p className="rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
                {t('admin.access.reset.superadminNote')}
              </p>
            ) : null}
            {account.kind === 'shop' ? (
              <p className="rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
                {t('admin.access.reset.shopNote')}
              </p>
            ) : null}

            <AccountSessionsPanel userId={account.userId} label={label} reason={reason} />
          </div>
        </Card>
      ) : null}

      <ResetAdminPasswordDialog
        target={
          resetting && account !== null && account !== undefined
            ? { userId: account.userId, label }
            : null
        }
        onClose={() => {
          setResetting(false);
        }}
      />

      <PasswordActionDialog
        open={unlocking}
        title={t('admin.access.unlockTitle')}
        warning={t('admin.access.unlockWarning')}
        confirmLabel={t('merchant.common.confirm')}
        subject={label}
        onClose={() => {
          setUnlocking(false);
        }}
        onConfirm={async ({ reason: motive, password }) => {
          if (account === null || account === undefined) return;
          await unlockMutation.mutateAsync({
            userId: account.userId,
            reason: motive,
            confirmPassword: password,
          });
          toast.success(t('admin.access.unlockedToast'));
          setUnlocking(false);
          setAccount({ ...account, lockedUntil: null });
        }}
      />
    </div>
  );
}
